import { api } from './api';
import type {
  CustomerExperienceConfig,
  SetCustomerProductModuleConfigPayload,
  TenantLicense,
  UpdateCustomerSystemConfigPayload,
} from '../types/tenantLicense';

const LICENSE_STORAGE_KEY = '@KnowInDoc:tenantLicense';

export const licenseService = {
  async fetchMyLicense(): Promise<TenantLicense> {
    const response = await api.get<TenantLicense>('/me/license');
    return response.data;
  },

  cacheLicense(license: TenantLicense): void {
    sessionStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(license));
  },

  getCachedLicense(): TenantLicense | null {
    const raw = sessionStorage.getItem(LICENSE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as TenantLicense;
    } catch {
      return null;
    }
  },

  clearCachedLicense(): void {
    sessionStorage.removeItem(LICENSE_STORAGE_KEY);
  },

  async refreshCachedLicense(): Promise<TenantLicense | null> {
    try {
      const license = await this.fetchMyLicense();
      this.cacheLicense(license);
      return license;
    } catch {
      return null;
    }
  },

  async getCustomerExperienceConfig(customerId: string): Promise<CustomerExperienceConfig> {
    const response = await api.get<CustomerExperienceConfig>(
      `/customers/${customerId}/system-config`
    );
    return response.data;
  },

  async updateCustomerSystemConfig(
    customerId: string,
    payload: UpdateCustomerSystemConfigPayload
  ): Promise<void> {
    await api.put(`/customers/${customerId}/system-config`, payload);
  },

  async setProductModuleConfig(
    customerId: string,
    moduleCode: string,
    payload: SetCustomerProductModuleConfigPayload
  ): Promise<void> {
    await api.put(
      `/customers/${customerId}/product-modules/${encodeURIComponent(moduleCode)}/config`,
      payload
    );
  },

  async deleteProductModuleConfig(customerId: string, moduleCode: string): Promise<void> {
    await api.delete(
      `/customers/${customerId}/product-modules/${encodeURIComponent(moduleCode)}/config`
    );
  },
};
